import React, { useState, useEffect } from "react";
import {
  Save,
  X,
  Pencil,
  BarChart2,
  Clock,
  Circle,
  Plus,
  Settings,
  Scale,
  Wrench,
  Snowflake,
  MapPin,
  Droplet,
  HeartPulse,
  RefreshCw,
} from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import { useAuth } from "../../auth/context/AuthContext";
import {
  ehMedico,
  ehMultiprofissional,
} from "../../../shared/utils/permissoesCategoria";

interface TecnicaOperatoriaProps {
  paciente: Paciente;
  onSalvarTx?: (dados: any) => void;
}
export default function TecnicaOperatoria({
  paciente,
  onSalvarTx,
}: TecnicaOperatoriaProps) {
  const { atualizarPaciente } = usePacientes();
  const { usuarioLogado } = useAuth();
  // 🔐 B88 (AI_98): dados do transplante sao campos CLINICOS do doc
  // pacientes - edicao restrita a equipe clinica (espelho das
  // firestore.rules). Nome legado: hoje significa "somente leitura"
  // (vale p/ convidado E administrativo).
  const isConvidado =
    !ehMedico(usuarioLogado) && !ehMultiprofissional(usuarioLogado);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [dados, setDados] = useState<{
    pesoEnxerto?: string;
    metodo?: string;
    clampamentoPorta?: string;
    figadoForaGelo?: string;
    revascularizacaoPortalHora?: string;
    revascularizacaoPortalLocal?: string;
    revascularizacaoArterialHora?: string;
    revascularizacaoArterialLocal?: string;
    drenagemVenosa?: string;
    drenagemVenosaDescricao?: string;
    drenagemCavaCava?: string;
    reconstrucaoBiliar?: string;
    editadoEm?: string;
  }>({});

  // ✅ CARREGAR dados
  useEffect(() => {
    const tec = paciente.dadosTransplante?.tecnicaOperatoria;
    if (tec && Object.values(tec).some((v) => v && String(v).trim() !== "")) {
      setDados(tec);
    } else {
      setDados({});
    }
  }, [paciente.id, paciente.dadosTransplante?.tecnicaOperatoria]);

  const temDados = Object.values(dados).some((v) => v && v.trim() !== "");

  const handleSalvar = () => {
    const temDadosPreenchidos = Object.entries(dados).some(
      ([k, v]) => k !== "editadoEm" && v && String(v).trim() !== ""
    );
    if (!temDadosPreenchidos) {
      alert("Preencha pelo menos um campo antes de salvar.");
      return;
    }
    const dadosComTimestamp = { ...dados, editadoEm: new Date().toISOString() };
    if (onSalvarTx) {
      onSalvarTx(dadosComTimestamp);
    } else {
      atualizarPaciente(paciente.id, {
        dadosTransplante: {
          ...paciente.dadosTransplante,
          tecnicaOperatoria: dadosComTimestamp,
        },
      });
    }
    setModoEdicao(false);
  };

  // Janela de 24h para edição
  const editadoEm = paciente.dadosTransplante?.tecnicaOperatoria?.editadoEm;
  const horasDecorridas = editadoEm
    ? (new Date().getTime() - new Date(editadoEm).getTime()) / (1000 * 60 * 60)
    : 0;
  const podeEditar = !temDados || horasDecorridas <= 24;

  // ⏱️ FUNÇÃO AUXILIAR: Calcular diferença entre horários
  const calcularDiferencaHoras = (
    horaInicio: string,
    horaFim: string
  ): string => {
    if (!horaInicio || !horaFim) return "-";

    const [h1, m1] = horaInicio.split(":").map(Number);
    const [h2, m2] = horaFim.split(":").map(Number);

    let totalMinutos = h2 * 60 + m2 - (h1 * 60 + m1);

    // Se negativo, assumir que passou da meia-noite
    if (totalMinutos < 0) {
      totalMinutos += 24 * 60;
    }

    const horas = Math.floor(totalMinutos / 60);
    const minutos = totalMinutos % 60;

    return `${horas}h ${minutos}min`;
  };

  // ⏱️ CÁLCULOS AUTOMÁTICOS
  const calcularFaseAnepatica = (): string => {
    return calcularDiferencaHoras(
      dados.clampamentoPorta || "",
      dados.revascularizacaoPortalHora || ""
    );
  };

  const calcularIsquemiaFria = (): string => {
    const clampeDoador =
      paciente.dadosTransplante?.dadosComplementares?.clampeDoador;
    return calcularDiferencaHoras(
      clampeDoador || "",
      dados.figadoForaGelo || ""
    );
  };

  const calcularIsquemiaMorna = (): string => {
    return calcularDiferencaHoras(
      dados.figadoForaGelo || "",
      dados.revascularizacaoPortalHora || ""
    );
  };

  const calcularIsquemiaPortal = (): string => {
    const clampeDoador =
      paciente.dadosTransplante?.dadosComplementares?.clampeDoador;
    return calcularDiferencaHoras(
      clampeDoador || "",
      dados.revascularizacaoPortalHora || ""
    );
  };

  const calcularIsquemiaArterial = (): string => {
    const clampeDoador =
      paciente.dadosTransplante?.dadosComplementares?.clampeDoador;
    return calcularDiferencaHoras(
      clampeDoador || "",
      dados.revascularizacaoArterialHora || ""
    );
  };

  const metodos = [
    "Convencional",
    "Piggy Back",
    "Piggy Back Modificado",
    "Com By Pass",
  ];

  const drenagemVenosaOpcoes = [
    "Veias Hepáticas / Veia Cava",
    "Veia Cava / Veia Cava",
  ];

  const drenagemCavaCavaOpcoes = [
    "Término - Lateral",
    "Latero - Lateral",
    "Convencional",
  ];

  const portalLocais = [
    "Veia Porta / Veia Porta",
    "Veia Renal / Veia Porta",
    "Veia Cava / Veia Porta",
    "Veia Esplênica / Veia Porta",
    "Veia Gástrica Esquerda / Veia Porta",
    "Com Enxerto Venoso",
  ];

  const arterialLocais = [
    "Artéria Hepática Esquerda",
    "Artéria Hepática Direita",
    "Bifurcação das Hepáticas",
    "Artéria Hepática Própria",
    "Bifurcação Artéria Hepática Comum e AGD",
    "Artéria Hepática Comum",
    "Artéria Esplênica",
    "Aorta",
    "Com Enxerto Arterial",
  ];

  const biliarOpcoes = [
    "Colédoco-Colédoco",
    "Bileo-Digestiva",
    "Ducto Hepático Direito - Colédoco",
    "Ducto Hepático Esquerdo - Colédoco",
  ];

  return (
    <div>
      {/* BOTÕES */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "16px",
          gap: "8px",
        }}
      >
        {!isConvidado && (
          <>
            {modoEdicao ? (
              <>
                <button
                  onClick={handleSalvar}
                  style={{
                    padding: "8px 16px",
                    background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                    color: "white",
                    border: "none",
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Save size={14} style={{ marginRight: "6px" }} /> Salvar e Ver
                  Resumo
                </button>
                <button
                  onClick={() => {
                    setModoEdicao(false);
                    if (paciente.dadosTransplante?.tecnicaOperatoria) {
                      setDados(paciente.dadosTransplante.tecnicaOperatoria);
                    }
                  }}
                  style={{
                    padding: "8px 16px",
                    background: theme.gray[300],
                    color: theme.gray[700],
                    border: "none",
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <X size={14} style={{ marginRight: "6px" }} /> Cancelar
                </button>
              </>
            ) : podeEditar ? (
              <button
                onClick={() => setModoEdicao(true)}
                style={{
                  padding: "8px 16px",
                  background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                  color: "white",
                  border: "none",
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Pencil size={14} style={{ marginRight: "6px" }} /> Editar
              </button>
            ) : null}
          </>
        )}
      </div>

      {/* MODO VISUALIZAÇÃO */}
      {!modoEdicao && temDados ? (
        <div>
          {/* DADOS BÁSICOS */}
          <div
            style={{
              background: `linear-gradient(135deg, ${theme.primary[50]} 0%, white 100%)`,
              border: `2px solid ${theme.primary[200]}`,
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
              marginBottom: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: theme.primary[700],
                marginBottom: "16px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <BarChart2 size={16} style={{ marginRight: "8px" }} /> Dados
              Básicos
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "12px",
              }}
            >
              {dados.pesoEnxerto && (
                <div
                  style={{
                    background: "white",
                    padding: "12px",
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.primary[200]}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      color: theme.primary[600],
                      marginBottom: "4px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    ⚖️ PESO DO ENXERTO
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: theme.primary[700],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {dados.pesoEnxerto} g
                  </div>
                </div>
              )}
              {dados.metodo && (
                <div
                  style={{
                    background: "white",
                    padding: "12px",
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.primary[200]}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      color: theme.primary[600],
                      marginBottom: "4px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    🔧 MÉTODO
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {dados.metodo}
                  </div>
                </div>
              )}
              {dados.clampamentoPorta && (
                <div
                  style={{
                    background: "white",
                    padding: "12px",
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.primary[200]}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      color: theme.primary[600],
                      marginBottom: "4px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Clock size={12} style={{ marginRight: "4px" }} /> CLAMPE
                    PORTA
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: theme.primary[700],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {dados.clampamentoPorta}
                  </div>
                </div>
              )}
              {dados.figadoForaGelo && (
                <div
                  style={{
                    background: "white",
                    padding: "12px",
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.primary[200]}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      color: theme.primary[600],
                      marginBottom: "4px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    🧊 FORA DO GELO
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: theme.primary[700],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {dados.figadoForaGelo}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* TEMPOS CALCULADOS */}
          <div
            style={{
              background: "#fef3c7",
              border: "2px solid #fbbf24",
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
              marginBottom: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#92400e",
                marginBottom: "16px",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              ⏱️ Tempos Calculados
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "12px",
              }}
            >
              <div
                style={{
                  background: "white",
                  padding: "16px",
                  borderRadius: theme.borderRadius.md,
                  border: "2px solid #fbbf24",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#92400e",
                    marginBottom: "6px",
                    fontFamily: theme.fonts.sans,
                    fontWeight: "600",
                  }}
                >
                  FASE ANEPÁTICA
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#92400e",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {calcularFaseAnepatica()}
                </div>
              </div>
              <div
                style={{
                  background: "white",
                  padding: "16px",
                  borderRadius: theme.borderRadius.md,
                  border: "2px solid #fbbf24",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#92400e",
                    marginBottom: "6px",
                    fontFamily: theme.fonts.sans,
                    fontWeight: "600",
                  }}
                >
                  ISQUEMIA FRIA
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#92400e",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {calcularIsquemiaFria()}
                </div>
              </div>
              <div
                style={{
                  background: "white",
                  padding: "16px",
                  borderRadius: theme.borderRadius.md,
                  border: "2px solid #fbbf24",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#92400e",
                    marginBottom: "6px",
                    fontFamily: theme.fonts.sans,
                    fontWeight: "600",
                  }}
                >
                  ISQUEMIA MORNA
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#92400e",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {calcularIsquemiaMorna()}
                </div>
              </div>
              <div
                style={{
                  background: "white",
                  padding: "16px",
                  borderRadius: theme.borderRadius.md,
                  border: "2px solid #fbbf24",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#92400e",
                    marginBottom: "6px",
                    fontFamily: theme.fonts.sans,
                    fontWeight: "600",
                  }}
                >
                  ISQUEMIA PORTAL
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#92400e",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {calcularIsquemiaPortal()}
                </div>
              </div>
              <div
                style={{
                  background: "white",
                  padding: "16px",
                  borderRadius: theme.borderRadius.md,
                  border: "2px solid #fbbf24",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#92400e",
                    marginBottom: "6px",
                    fontFamily: theme.fonts.sans,
                    fontWeight: "600",
                  }}
                >
                  ISQUEMIA ARTERIAL
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#92400e",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {calcularIsquemiaArterial()}
                </div>
              </div>
            </div>
          </div>

          {/* REVASCULARIZAÇÕES */}
          <div
            style={{
              background: `linear-gradient(135deg, ${theme.primary[50]} 0%, white 100%)`,
              border: `2px solid ${theme.primary[200]}`,
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
              marginBottom: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: theme.primary[700],
                marginBottom: "16px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <RefreshCw size={16} style={{ marginRight: "8px" }} />{" "}
              Revascularizações
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
              }}
            >
              {(dados.revascularizacaoPortalHora ||
                dados.revascularizacaoPortalLocal) && (
                <div>
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "8px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Circle
                      size={14}
                      color="#3b82f6"
                      style={{ marginRight: "6px" }}
                    />{" "}
                    Revascularização Portal
                  </h4>
                  <div
                    style={{
                      background: "white",
                      padding: "12px",
                      borderRadius: theme.borderRadius.md,
                      border: `1px solid ${theme.primary[200]}`,
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <span
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        HORA:
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.primary[700],
                          marginLeft: "8px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.revascularizacaoPortalHora || "-"}
                      </span>
                    </div>
                    <div>
                      <span
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        LOCAL:
                      </span>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: theme.primary[700],
                          marginTop: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.revascularizacaoPortalLocal || "-"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {(dados.revascularizacaoArterialHora ||
                dados.revascularizacaoArterialLocal) && (
                <div>
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "8px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Circle
                      size={14}
                      color="#ef4444"
                      style={{ marginRight: "6px" }}
                    />{" "}
                    Revascularização Arterial
                  </h4>
                  <div
                    style={{
                      background: "white",
                      padding: "12px",
                      borderRadius: theme.borderRadius.md,
                      border: `1px solid ${theme.primary[200]}`,
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <span
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        HORA:
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.primary[700],
                          marginLeft: "8px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.revascularizacaoArterialHora || "-"}
                      </span>
                    </div>
                    <div>
                      <span
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        LOCAL:
                      </span>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: theme.primary[700],
                          marginTop: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.revascularizacaoArterialLocal || "-"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* DRENAGEM + BILIAR */}
          <div
            style={{
              background: `linear-gradient(135deg, ${theme.primary[50]} 0%, white 100%)`,
              border: `2px solid ${theme.primary[200]}`,
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
              }}
            >
              {dados.drenagemVenosa && (
                <div>
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "8px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Droplet
                      size={14}
                      color="#ef4444"
                      style={{ marginRight: "6px" }}
                    />{" "}
                    Drenagem Venosa
                  </h4>
                  <div
                    style={{
                      background: "white",
                      padding: "12px",
                      borderRadius: theme.borderRadius.md,
                      border: `1px solid ${theme.primary[200]}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: theme.primary[700],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {dados.drenagemVenosa}
                      {dados.drenagemVenosaDescricao &&
                        ` - ${dados.drenagemVenosaDescricao}`}
                      {dados.drenagemCavaCava && ` (${dados.drenagemCavaCava})`}
                    </div>
                  </div>
                </div>
              )}
              {dados.reconstrucaoBiliar && (
                <div>
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "8px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <HeartPulse
                      size={14}
                      color="#f59e0b"
                      style={{ marginRight: "6px" }}
                    />{" "}
                    Reconstrução Biliar
                  </h4>
                  <div
                    style={{
                      background: "white",
                      padding: "12px",
                      borderRadius: theme.borderRadius.md,
                      border: `1px solid ${theme.primary[200]}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: theme.primary[700],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {dados.reconstrucaoBiliar}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : !modoEdicao && !temDados ? (
        // ESTADO VAZIO
        <div
          style={{
            textAlign: "center",
            padding: "48px 20px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.lg,
            border: `2px dashed ${theme.gray[300]}`,
          }}
        >
          <Settings
            size={48}
            color={theme.gray[300]}
            style={{ marginBottom: "16px" }}
          />
          <p
            style={{
              color: theme.gray[600],
              fontSize: "14px",
              fontFamily: theme.fonts.sans,
              marginBottom: "16px",
            }}
          >
            Técnica operatória não cadastrada ainda
          </p>
          {!isConvidado && (
            <button
              onClick={() => setModoEdicao(true)}
              style={{
                padding: "10px 20px",
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Plus size={14} style={{ marginRight: "6px" }} /> Cadastrar
              Técnica
            </button>
          )}
        </div>
      ) : (
        // MODO EDIÇÃO
        <div>
          {/* DADOS BÁSICOS */}
          <div style={{ marginBottom: "24px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: theme.primary[700],
                marginBottom: "12px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <BarChart2 size={16} style={{ marginRight: "8px" }} /> Dados
              Básicos
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Scale size={14} style={{ marginRight: "6px" }} /> Peso do
                  Enxerto (gramas)
                </label>
                <input
                  type="number"
                  value={dados.pesoEnxerto || ""}
                  onChange={(e) =>
                    setDados({ ...dados, pesoEnxerto: e.target.value })
                  }
                  placeholder="1500"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Wrench size={14} style={{ marginRight: "6px" }} /> Método
                </label>
                <select
                  value={dados.metodo || ""}
                  onChange={(e) =>
                    setDados({ ...dados, metodo: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                >
                  <option value="">Selecione...</option>
                  {metodos.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Clock size={14} style={{ marginRight: "6px" }} />{" "}
                  Clampeamento da Porta
                </label>
                <input
                  type="time"
                  value={dados.clampamentoPorta || ""}
                  onChange={(e) =>
                    setDados({ ...dados, clampamentoPorta: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Snowflake size={14} style={{ marginRight: "6px" }} /> Fígado
                  Novo Fora do Gelo
                </label>
                <input
                  type="time"
                  value={dados.figadoForaGelo || ""}
                  onChange={(e) =>
                    setDados({ ...dados, figadoForaGelo: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
          </div>

          {/* REVASCULARIZAÇÃO PORTAL (ANTES DA ARTERIAL) */}
          <div style={{ marginBottom: "24px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: theme.primary[700],
                marginBottom: "12px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Circle
                size={14}
                color="#3b82f6"
                style={{ marginRight: "6px" }}
              />{" "}
              Revascularização Portal
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 2fr",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Clock size={14} style={{ marginRight: "6px" }} /> Hora
                </label>
                <input
                  type="time"
                  value={dados.revascularizacaoPortalHora || ""}
                  onChange={(e) =>
                    setDados({
                      ...dados,
                      revascularizacaoPortalHora: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <MapPin size={14} style={{ marginRight: "6px" }} /> Local
                </label>
                <select
                  value={dados.revascularizacaoPortalLocal || ""}
                  onChange={(e) =>
                    setDados({
                      ...dados,
                      revascularizacaoPortalLocal: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                >
                  <option value="">Selecione...</option>
                  {portalLocais.map((local) => (
                    <option key={local} value={local}>
                      {local}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* REVASCULARIZAÇÃO ARTERIAL (DEPOIS DA PORTAL) */}
          <div style={{ marginBottom: "24px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: theme.primary[700],
                marginBottom: "12px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Circle
                size={14}
                color="#ef4444"
                style={{ marginRight: "6px" }}
              />{" "}
              Revascularização Arterial
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 2fr",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Clock size={14} style={{ marginRight: "6px" }} /> Hora
                </label>
                <input
                  type="time"
                  value={dados.revascularizacaoArterialHora || ""}
                  onChange={(e) =>
                    setDados({
                      ...dados,
                      revascularizacaoArterialHora: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <MapPin size={14} style={{ marginRight: "6px" }} /> Local no
                  Receptor
                </label>
                <select
                  value={dados.revascularizacaoArterialLocal || ""}
                  onChange={(e) =>
                    setDados({
                      ...dados,
                      revascularizacaoArterialLocal: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                >
                  <option value="">Selecione...</option>
                  {arterialLocais.map((local) => (
                    <option key={local} value={local}>
                      {local}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* DRENAGEM VENOSA */}
          <div style={{ marginBottom: "24px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: theme.primary[700],
                marginBottom: "12px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Droplet
                size={14}
                color="#ef4444"
                style={{ marginRight: "6px" }}
              />{" "}
              Drenagem Venosa
            </h3>
            <div style={{ display: "grid", gap: "16px" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Tipo de Drenagem
                </label>
                <select
                  value={dados.drenagemVenosa || ""}
                  onChange={(e) =>
                    setDados({ ...dados, drenagemVenosa: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                >
                  <option value="">Selecione...</option>
                  {drenagemVenosaOpcoes.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
              </div>

              {dados.drenagemVenosa === "Veias Hepáticas / Veia Cava" && (
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: theme.gray[700],
                      marginBottom: "8px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Descrição
                  </label>
                  <input
                    type="text"
                    value={dados.drenagemVenosaDescricao || ""}
                    onChange={(e) =>
                      setDados({
                        ...dados,
                        drenagemVenosaDescricao: e.target.value,
                      })
                    }
                    placeholder="Descrição da drenagem"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${theme.gray[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              )}

              {dados.drenagemVenosa === "Veia Cava / Veia Cava" && (
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: theme.gray[700],
                      marginBottom: "8px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Tipo de Anastomose
                  </label>
                  <select
                    value={dados.drenagemCavaCava || ""}
                    onChange={(e) =>
                      setDados({ ...dados, drenagemCavaCava: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${theme.gray[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="">Selecione...</option>
                    {drenagemCavaCavaOpcoes.map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* RECONSTRUÇÃO BILIAR */}
          <div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: theme.primary[700],
                marginBottom: "12px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <HeartPulse
                size={14}
                color="#f59e0b"
                style={{ marginRight: "6px" }}
              />{" "}
              Reconstrução Biliar
            </h3>
            <select
              value={dados.reconstrucaoBiliar || ""}
              onChange={(e) =>
                setDados({ ...dados, reconstrucaoBiliar: e.target.value })
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `2px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontFamily: theme.fonts.sans,
                boxSizing: "border-box",
              }}
            >
              <option value="">Selecione...</option>
              {biliarOpcoes.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
